import { useEditProtection } from '../../shared/components/EditProtection';
import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  scanInvoice,
  createReceivingInvoice,
  createItem,
  createSupplier,
  listSuppliers,
  listItems,
  InvoiceScanResult,
  InventoryItem,
  Supplier,
} from './api';
import './inventory-v2.css';

// ── Types ─────────────────────────────────────────────────────────────────────

type Step = 'upload' | 'reviewing';

interface LineState {
  include: boolean;
  matched_item_id: number | 'new' | '';
  quantity: string;
  unit: string;
  unit_price: string;
  conversion: string;
  // new item creation fields
  new_name: string;
  new_sku: string;
  creating: boolean;
  created_item_id: number | null;
}

const UNIT_OPTIONS = ['gram', 'kg', 'ml', 'litre', 'count', 'bag', 'can', 'case', 'box', 'pack', 'lid', 'cup'];

/** Normalize common abbreviations to the API's valid base_unit values */
const UNIT_NORMALIZE: Record<string, string> = {
  g: 'gram', gm: 'gram', grams: 'gram',
  kilogram: 'kg', kilograms: 'kg', kilo: 'kg',
  millilitre: 'ml', milliliter: 'ml', millilitres: 'ml', milliliters: 'ml',
  liter: 'litre', litres: 'litre', liters: 'litre', l: 'litre',
  each: 'count', ea: 'count', unit: 'count', units: 'count', piece: 'count', pieces: 'count', pcs: 'count',
  bags: 'bag', cans: 'can', cases: 'case', boxes: 'box', packs: 'pack', pk: 'pack',
  lids: 'lid', cups: 'cup',
};
const VALID_UNITS = new Set(UNIT_OPTIONS);

/** Try to normalize a raw unit string to a valid API value. Returns the mapped value or the raw string if unrecognizable. */
function tryNormalizeUnit(raw: string): string {
  const lower = raw.trim().toLowerCase();
  const mapped = UNIT_NORMALIZE[lower] || lower;
  return VALID_UNITS.has(mapped) ? mapped : raw.trim();
}

/** Check if a unit value (after any normalization attempt) is valid for the API */
function isUnitValid(unit: string): boolean {
  const lower = unit.trim().toLowerCase();
  const mapped = UNIT_NORMALIZE[lower] || lower;
  return VALID_UNITS.has(mapped);
}

function toDateInput(raw: string | null): string {
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

const InvoiceScan: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('upload');
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');

  const [scanResult, setScanResult] = useState<InvoiceScanResult | null>(null);
  const [lineStates, setLineStates] = useState<LineState[]>([]);
  const [allItems, setAllItems] = useState<InventoryItem[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  const [supplierOverride, setSupplierOverride] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [notes, setNotes] = useState('');
  const [stockEffect,setStockEffect]=useState('receive_now');
  const [pendingInvoice,setPendingInvoice]=useState<any>(null);
  const saveBusy=useRef(false);
  const [saving, setSaving] = useState(false);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [needsReview, setNeedsReview] = useState(false);
  const [focusLine, setFocusLine] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);
  useEditProtection(!saved && (scanning || step === 'reviewing'));

  // ── Helpers ──────────────────────────────────────────────────────────────
  const patch = (idx: number, p: Partial<LineState>) =>
    setLineStates(prev => prev.map((s, i) => i === idx ? { ...s, ...p } : s));

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/tiff'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF, JPEG, PNG, or TIFF file.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File is too large (max 10 MB).');
      return;
    }

    setError('');
    setScanning(true);

    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => { resolve((reader.result as string).split(',')[1]); };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const [result, itemsRes, suppliersRes] = await Promise.all([
        scanInvoice(sessionToken, base64, file.type),
        listItems(sessionToken),
        listSuppliers(sessionToken),
      ]);

      setScanResult(result);
      setAllItems(itemsRes.items);
      setSuppliers(suppliersRes.suppliers);
      setInvoiceNumber(result.invoice_number || '');
      setInvoiceDate(toDateInput(result.invoice_date));
      setSupplierOverride(result.supplier || '');

      setLineStates(result.items.map(item => ({
        include: true,
        matched_item_id: item.matched_item?.id ?? '',
        quantity: item.quantity != null ? String(item.quantity) : '',
        unit: item.matched_item?.base_unit || tryNormalizeUnit(item.unit || 'count'),
        conversion: '1',
        unit_price: item.amount != null && item.quantity ? String(item.amount / item.quantity) : item.unit_price != null ? String(item.unit_price) : '',
        new_name: item.description || '',
        new_sku: item.product_code || '',
        creating: false,
        created_item_id: null,
      })));

      setStep('reviewing');
    } catch (err: any) {
      setError(err.message || 'Failed to scan invoice');
    } finally {
      setScanning(false);
    }
  };

  // ── Create a single new item inline ──────────────────────────────────────
  const handleCreateNewItem = async (idx: number) => {
    const ls = lineStates[idx];
    if (!ls.new_name.trim()) { setError('Item name is required'); return; }
    if (!isUnitValid(ls.unit || 'count')) { setError('Please select a valid unit before creating'); return; }
    patch(idx, { creating: true });
    try {
      const res = await createItem(sessionToken, {
        name: ls.new_name.trim(),
        base_unit: tryNormalizeUnit(ls.unit || 'count'),
        supplier_sku: ls.new_sku || null,
        purchase_cost: ls.unit_price ? Number(ls.unit_price) : null,
        on_hand_quantity: 0,
        notes: `Created from scanned invoice${invoiceNumber ? ` #${invoiceNumber}` : ''}`,
      } as any);
      const newItem = res.item;
      setAllItems(prev => [...prev, newItem]);
      patch(idx, { creating: false, created_item_id: newItem.id, matched_item_id: newItem.id, unit: newItem.base_unit || ls.unit, include: true });
    } catch (err: any) {
      patch(idx, { creating: false });
      setError(`Failed to create item: ${err.message}`);
    }
  };

  // ── Bulk create all unmatched ─────────────────────────────────────────────
  const unmatchedIndices = lineStates
    .map((ls, idx) => ({ ls, idx }))
    .filter(({ ls }) => ls.include && !ls.matched_item_id && !ls.created_item_id && ls.new_name.trim());

  const handleAddAllUnmatched = async () => {
    // Check if any items have invalid units — skip those and warn the user
    const invalidUnitItems = unmatchedIndices.filter(({ ls }) => !isUnitValid(ls.unit || 'count'));
    if (invalidUnitItems.length > 0 && invalidUnitItems.length === unmatchedIndices.length) {
      setError(`All ${invalidUnitItems.length} items have unrecognized units (highlighted in red). Please select a valid unit for each.`);
      return;
    }

    setBulkCreating(true); setError(''); setSuccessMsg('');
    let created = 0;
    let skippedUnits = 0;
    for (const { idx } of unmatchedIndices) {
      const ls = lineStates[idx];
      if (!isUnitValid(ls.unit || 'count')) { skippedUnits++; continue; }
      patch(idx, { creating: true });
      try {
        const res = await createItem(sessionToken, {
          name: ls.new_name.trim(), base_unit: tryNormalizeUnit(ls.unit || 'count'),
          supplier_sku: ls.new_sku || null,
          purchase_cost: ls.unit_price ? Number(ls.unit_price) : null,
          on_hand_quantity: 0,
        } as any);
        const newItem = res.item;
        setAllItems(prev => [...prev, newItem]);
        patch(idx, { creating: false, created_item_id: newItem.id, matched_item_id: newItem.id, unit: newItem.base_unit || ls.unit });
        created++;
      } catch { patch(idx, { creating: false }); }
    }
    setBulkCreating(false);
    if (created > 0) {
      let msg = `✅ ${created} item${created > 1 ? 's' : ''} added to inventory and matched!`;
      if (skippedUnits > 0) msg += ` ⚠️ ${skippedUnits} skipped — please fix their unit first.`;
      setSuccessMsg(msg);
    } else if (skippedUnits > 0) {
      setError(`All ${skippedUnits} items have unrecognized units. Please select a valid unit for each (highlighted in red).`);
    }
  };

  // ── Save receiving invoice ────────────────────────────────────────────────
  const includedLines = lineStates
    .map((ls, idx) => ({ ls, item: scanResult?.items[idx] }))
    .filter(({ ls }) => ls.include && ls.matched_item_id && ls.matched_item_id !== 'new');

  const handleSave = async () => {
    if (!scanResult || saveBusy.current) return;
    setError(''); setSuccessMsg('');
    if (!pendingInvoice && (!supplierOverride.trim() || !invoiceDate || !invoiceNumber.trim())) { setError('Enter a supplier, invoice number and invoice date.'); return; }
    if (!pendingInvoice && scanResult.currency && scanResult.currency !== 'CAD') { setError('Only CAD invoices are supported. Enter converted CAD costs using manual receiving.'); return; }
    if (!pendingInvoice && lineStates.some(ls => ls.include && (!ls.matched_item_id || ls.matched_item_id === 'new'))) {setError('Match or explicitly skip every invoice line before saving.');return;}
    if (!pendingInvoice && includedLines.some(({ls}) => !/^\d{1,8}(\.\d{1,4})?$/.test(ls.quantity) || Number(ls.quantity)<=0 || !/^\d{1,8}(\.\d{1,6})?$/.test(ls.unit_price) || !Number.isFinite(Number(ls.conversion)) || Number(ls.conversion)<=0)) {setError('Check received quantities, prices and unit conversion on every included line.');return;}
    saveBusy.current=true;setSaving(true);
    try {
      let request=pendingInvoice;
      if (!request) {
        let selected=suppliers.find(s=>s.name.toLowerCase()===supplierOverride.trim().toLowerCase());
        if (!selected) {const result=await createSupplier(sessionToken,{name:supplierOverride.trim()});selected=result.supplier;setSuppliers(old=>[...old,result.supplier]);}
        const lines=includedLines.map(({ls,item})=>({
          inventory_item_id:Number(ls.matched_item_id),raw_item_name:item?.description || '',
          quantity_received:Number((Number(ls.quantity)*Number(ls.conversion)).toFixed(4)),
          unit:allItems.find(i=>i.id===Number(ls.matched_item_id))?.base_unit || ls.unit,
          purchase_cost:Number((Number(ls.quantity)*Number(ls.unit_price)).toFixed(4)),
          notes:`Invoice quantity ${ls.quantity}; stock units per invoice unit ${ls.conversion}`,
        }));
        request={request_id:crypto.randomUUID(),supplier_id:selected.id,invoice_number:invoiceNumber.trim(),invoice_date:invoiceDate,
          invoice_file_id:scanResult.invoice_file_id,stock_effect:stockEffect,notes:notes || 'Imported from scanned invoice',lines};
        setPendingInvoice(request);
      }
      const result=await createReceivingInvoice(sessionToken,request);
      setSaved(true);
      setTimeout(()=>navigate(`/inventory/receiving/${result.invoice.id}`),0);
    } catch(err:any) {
      if(err.status>=400 && err.status<500)setPendingInvoice(null);
      setError(err.message || 'Unable to confirm invoice creation. Retry the same invoice.');
    } finally {saveBusy.current=false;setSaving(false);}
  };

  if (!['Owner', 'Admin', 'Manager'].includes(user?.role || ''))
    return <div className="inv2-error">Access denied.</div>;

  return (
    <div className="inv2-page inv2-scan-page">

      {/* ── Header ── */}
      <div className="inv2-scan-top-bar">
        <Link to="/inventory/receiving" className="inv2-back-link">← Receiving</Link>
        <h1 className="inv2-scan-title">🔍 Scan Invoice</h1>
      </div>

      {error && <div className="inv2-error">{error}</div>}
      {successMsg && <div className="inv2-scan-success">{successMsg}</div>}

      {/* ════════ STEP 1: UPLOAD ════════ */}
      {step === 'upload' && (
        <div className="inv2-section">
          {scanning ? (
            <div className="inv2-scan-loading">
              <div className="inv2-spinner" />
              <h3>Reading invoice…</h3>
              <p>Azure AI is scanning your invoice.<br />This takes about 15–30 seconds.</p>
            </div>
          ) : (
            <>
              <label className="inv2-scan-drop-label">
                <div className="inv2-scan-drop-icon">📄</div>
                <span className="inv2-scan-drop-title">Choose invoice file</span>
                <span className="inv2-scan-drop-sub">PDF · JPEG · PNG · TIFF — max 10 MB</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff"
                  onChange={handleFile} className="inv2-file-input" />
              </label>
              <label className="inv2-btn">Take photo<input type="file" aria-label="Take invoice photo" accept="image/jpeg,image/png" capture="environment" onChange={handleFile} /></label>
              <ul className="inv2-scan-tips">
                <li>📸 A clear photo of the invoice works fine</li>
                <li>📋 Multi-page PDFs supported</li>
                <li>🔤 Works best when items are in a table or list</li>
              </ul>
            </>
          )}
        </div>
      )}

      {/* ════════ STEP 2: REVIEW ════════ */}
      {step === 'reviewing' && scanResult && (
        <>
          <fieldset disabled={saving || !!pendingInvoice} style={{border:0,padding:0,minWidth:0}}>
          {/* Invoice header */}
          <div className="inv2-scan-header-card">
            <div className="inv2-scan-field">
              <label>Supplier</label>
              <input className="inv2-input" value={supplierOverride}
                onChange={e => setSupplierOverride(e.target.value)}
                placeholder="Supplier name" list="scan-suppliers" />
              <datalist id="scan-suppliers">
                {suppliers.map(s => <option key={s.id} value={s.name} />)}
              </datalist>
            </div>
            <div className="inv2-scan-field-row">
              <div className="inv2-scan-field">
                <label>Invoice #</label>
                <input className="inv2-input" value={invoiceNumber}
                  onChange={e => setInvoiceNumber(e.target.value)} placeholder="INV-001" />
              </div>
              <div className="inv2-scan-field">
                <label>Date</label>
                <input className="inv2-input" type="date" value={invoiceDate}
                  onChange={e => setInvoiceDate(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Summary + bulk action */}
          <div className="inv2-scan-summary-bar">
            <div className="inv2-scan-summary-badges">
              <span className="inv2-badge inv2-badge-success">
                ✅ {lineStates.filter(ls => ls.matched_item_id && ls.matched_item_id !== 'new').length} matched
              </span>
              <span className="inv2-badge inv2-badge-warn">
                ⚠️ {lineStates.filter(ls => !ls.matched_item_id && !ls.created_item_id).length} unmatched
              </span>
              <span className="inv2-badge inv2-badge-info">📋 {scanResult.items.length} items</span>
            </div>
            {unmatchedIndices.length > 0 && (
              <button className="inv2-btn inv2-btn-accent" onClick={handleAddAllUnmatched} disabled={bulkCreating}>
                {bulkCreating ? '⏳ Creating…' : `➕ Add all ${unmatchedIndices.length} unmatched to inventory`}
              </button>
            )}
          </div>

          <p>Creating inventory items takes effect immediately. Saving this invoice creates a draft; stock quantities change only when you post it.</p>
          <div className="cs-toolbar cs-sticky"><label><input type="checkbox" checked={needsReview} onChange={e => { setNeedsReview(e.target.checked); setFocusLine(null); }} /> Needs review only</label><button onClick={() => { setNeedsReview(false); setFocusLine(focusLine === null ? 0 : null); }}>{focusLine === null ? 'Review one line at a time' : 'Show all lines'}</button>{focusLine !== null && <><button disabled={focusLine === 0} onClick={() => setFocusLine(focusLine - 1)}>Previous</button><span>Line {focusLine + 1} of {lineStates.length}</span><button disabled={focusLine >= lineStates.length - 1} onClick={() => setFocusLine(focusLine + 1)}>Next</button></>}</div>
          {/* Line item cards */}
          <div className="inv2-scan-cards">
            {scanResult.items.map((item, idx) => {
              const ls = lineStates[idx];
              if (!ls || (focusLine !== null && idx !== focusLine) || (needsReview && (!ls.include || (ls.matched_item_id && ls.matched_item_id !== 'new' && Number(ls.quantity) > 0 && isUnitValid(ls.unit))))) return null;
              const isUnmatched = !ls.matched_item_id && !ls.created_item_id;
              const isCreatingNew = ls.matched_item_id === 'new';
              const isCreated = !!ls.created_item_id;

              return (
                <div key={idx} className={[
                  'inv2-scan-card',
                  !ls.include ? 'inv2-scan-card--skip' : '',
                  isUnmatched ? 'inv2-scan-card--unmatched' : '',
                  isCreated ? 'inv2-scan-card--new' : '',
                ].filter(Boolean).join(' ')}>

                  {/* Card header */}
                  <div className="inv2-scan-card-head">
                    <input type="checkbox" className="inv2-scan-check"
                      checked={ls.include} onChange={e => patch(idx, { include: e.target.checked })} />
                    <div className="inv2-scan-card-desc">
                      <span className="inv2-scan-card-name">{item.description || '—'}</span>
                      {item.product_code && <span className="inv2-scan-card-sku">SKU: {item.product_code}</span>}
                    </div>
                    {isCreated && <span className="inv2-badge inv2-badge-success">✅ Added</span>}
                    {isUnmatched && !isCreatingNew && <span className="inv2-badge inv2-badge-warn">No match</span>}
                  </div>

                  {/* Numbers */}
                  <div className="inv2-scan-card-nums">
                    <div className="inv2-scan-num-field">
                      <label>Invoice quantity</label>
                      <input inputMode="decimal" type="number" className="inv2-input" value={ls.quantity}
                        min="0" step="0.001" onChange={e => patch(idx, { quantity: e.target.value })} />
                    </div>
                    <div className={`inv2-scan-num-field ${!isUnitValid(ls.unit || '') && ls.unit ? 'inv2-scan-unit-invalid' : ''}`}>
                      <label>Unit {!isUnitValid(ls.unit || '') && ls.unit && <span className="inv2-unit-warn">⚠️</span>}</label>
                      {!isUnitValid(ls.unit || '') && ls.unit ? (
                        <>
                          <select className="inv2-select inv2-select-warn"
                            value=""
                            onChange={e => patch(idx, { unit: e.target.value })}>
                            <option value="" disabled>"{ls.unit}" → pick valid unit</option>
                            {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </>
                      ) : (
                        <>
                          <input disabled={!!ls.matched_item_id && ls.matched_item_id!=='new'} list={`ul-${idx}`} className="inv2-input" value={ls.unit}
                            onChange={e => patch(idx, { unit: e.target.value })} />
                          <datalist id={`ul-${idx}`}>{UNIT_OPTIONS.map(u => <option key={u} value={u} />)}</datalist>
                        </>
                      )}
                    </div>
                    <div className="inv2-scan-num-field">
                      <label>Net price / invoice unit (CAD)</label>
                      <input inputMode="decimal" type="number" className="inv2-input" value={ls.unit_price}
                        min="0" step="0.0001" onChange={e => patch(idx, { unit_price: e.target.value })} />
                    </div>
                    {ls.quantity && ls.unit_price && (
                      <div className="inv2-scan-num-field inv2-scan-total">
                        <label>Total</label>
                        <span>${(Number(ls.quantity) * Number(ls.unit_price)).toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  {ls.matched_item_id && ls.matched_item_id !== 'new' && <div className="inv2-scan-field"><label>Stock units per invoice unit<input aria-label={`Stock conversion line ${idx+1}`} className="inv2-input" inputMode="decimal" value={ls.conversion} onChange={e=>patch(idx,{conversion:e.target.value})}/></label><small>Receiving {Number(ls.quantity || 0)*Number(ls.conversion || 0)} {allItems.find(i=>i.id===Number(ls.matched_item_id))?.base_unit}. Pack size: {allItems.find(i=>i.id===Number(ls.matched_item_id))?.purchase_unit || 'Check the item unit'}. Confirm this conversion against the invoice.</small></div>}
                  {/* Match selector */}
                  {!isCreated && (
                    <div className="inv2-scan-card-match">
                      <label>Inventory item</label>
                      <select className="inv2-select" value={String(ls.matched_item_id)}
                        onChange={e => {
                          const val = e.target.value;
                          const found = allItems.find(i => String(i.id) === val);
                          patch(idx, {
                            matched_item_id: val === 'new' ? 'new' : (val === '' ? '' : Number(val)),
                            unit: found?.base_unit || ls.unit,
                            include: val !== '',
                          });
                        }}>
                        <option value="">— Skip this item —</option>
                        <option value="new">➕ Create new inventory item</option>
                        <optgroup label="Existing items">
                          {allItems.map(i => <option key={i.id} value={i.id}>{i.name} ({i.base_unit})</option>)}
                        </optgroup>
                      </select>
                    </div>
                  )}

                  {/* Inline new-item form */}
                  {isCreatingNew && (
                    <div className="inv2-scan-new-item-form">
                      <p className="inv2-scan-new-item-title">✏️ New item details</p>
                      <div className="inv2-scan-field">
                        <label>Item name *</label>
                        <input className="inv2-input" value={ls.new_name}
                          onChange={e => patch(idx, { new_name: e.target.value })}
                          placeholder="e.g. Whole Milk 3.25%" />
                      </div>
                      <div className="inv2-scan-field-row">
                        <div className="inv2-scan-field">
                          <label>Supplier SKU</label>
                          <input className="inv2-input" value={ls.new_sku}
                            onChange={e => patch(idx, { new_sku: e.target.value })} placeholder="Optional" />
                        </div>
                        <div className="inv2-scan-field">
                          <label>Base unit</label>
                          <input list={`nu-${idx}`} className="inv2-input" value={ls.unit}
                            onChange={e => patch(idx, { unit: e.target.value })} />
                          <datalist id={`nu-${idx}`}>{UNIT_OPTIONS.map(u => <option key={u} value={u} />)}</datalist>
                        </div>
                      </div>
                      <div className="inv2-scan-new-item-actions">
                        <button className="inv2-btn inv2-btn-primary"
                          onClick={() => handleCreateNewItem(idx)}
                          disabled={ls.creating || !ls.new_name.trim()}>
                          {ls.creating ? '⏳ Creating…' : '✅ Create & add'}
                        </button>
                        <button className="inv2-btn"
                          onClick={() => patch(idx, { matched_item_id: '', include: false })}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Notes */}
          <div className="inv2-scan-notes">
            <label className="inv2-label">Notes</label>
            <textarea className="inv2-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>

          <label className="inv2-label">Stock treatment<select className="inv2-select" value={stockEffect} onChange={e=>setStockEffect(e.target.value)}><option value="receive_now">New delivery — add to available stock when posted</option><option value="history_only">Already counted — record price history only</option></select></label>
          </fieldset>
          {pendingInvoice && !saving && <p role="status">Retry keeps this invoice unchanged and prevents duplicate creation.</p>}
          {/* Sticky action bar */}
          <div className="inv2-scan-action-bar">
            <button className="inv2-btn"
              disabled={saving || !!pendingInvoice} onClick={() => { setStep('upload'); setScanResult(null); }}>
              🔄 Rescan
            </button>
            <button className="inv2-btn inv2-btn-primary inv2-scan-save-btn"
              onClick={handleSave} disabled={saving || includedLines.length === 0}>
              {saving ? 'Saving…' : pendingInvoice ? 'Retry same invoice' : `Create Invoice (${includedLines.length} items)`}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default InvoiceScan;

